import {createApp} from 'vue';
import {VueQueryPlugin} from '@tanstack/vue-query';
import * as Sentry from '@sentry/vue';
import {createPinia} from 'pinia';
import App from '@/App.vue';
import '@/assets/styles/index.css';
/**
 * vue-sonner does not inject its own styles. Without this the toast is unstyled text at
 * the foot of the document and `position` has nothing to act on.
 */
import 'vue-sonner/style.css';
import {applyLocale, i18n, preferredLocale} from '@/i18n';
import {configureAmplify} from '@/lib/amplify';
import {expireSession, onSessionExpired} from '@/lib/session-expiry';
import {router} from '@/router';
import {useSessionStore} from '@/stores/session';

configureAmplify();

// Before the first render, so the login screen is already in the right language and the
// `lang` attribute matches what is on the page.
applyLocale(preferredLocale());

const app = createApp(App);

if (import.meta.env.VITE_SENTRY_ENABLED === 'true' && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_STAGE,
    sendDefaultPii: false,
  });
}

app.use(createPinia());
app.use(i18n);
app.use(router);
app.use(VueQueryPlugin, {
  queryClientConfig: {
    defaultOptions: {queries: {retry: 1, staleTime: 30_000, refetchOnWindowFocus: false}},
  },
});
// After pinia and the router, because the handler reaches for both. The store is
// resolved inside the callback rather than here: at this point nothing has installed it.
onSessionExpired(() => expireSession({session: useSessionStore(), router}));

app.mount('#app');
